import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Loader2,
  ReceiptText,
  Truck,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { rowHeading } from '@/lib/activityRowId';
import { ContactUsMenu } from '@/components/layout/ContactUsMenu';
import { ReorderButton, reorderMessage } from '@/components/orders/ReorderButton';
import type { ReorderSummary } from '@/hooks/useReorder';
import {
  useActivity,
  type ActivityRow,
  type ActivityStatus,
} from '@/hooks/useActivity';

/**
 * OrderHistory — the submitted-order ledger with live delivery status.
 *
 * Extracted from the former /activity page so it can render inside the
 * "Order history" tab of the consolidated Shop page (src/pages/Order.tsx).
 * It intentionally does NOT render the page chrome (SectionTabs); the host
 * supplies the surrounding layout.
 */

type Filter = 'all' | 'in_flight' | 'invoiced' | 'cancelled';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'in_flight', label: 'In flight' },
  { id: 'invoiced', label: 'Invoiced' },
  { id: 'cancelled', label: 'Cancelled' },
];

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function OrderHistory() {
  const [filter, setFilter] = useState<Filter>('all');
  const [reorderNotice, setReorderNotice] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);
  const { data: rows, isLoading, error } = useActivity();

  function handleReorderResult(summary: ReorderSummary | null) {
    setReorderNotice({
      tone: summary && summary.added > 0 ? 'success' : 'warning',
      text: reorderMessage(summary),
    });
  }

  const filtered = useMemo(() => {
    const all = rows ?? [];
    if (filter === 'cancelled') return all.filter((r) => r.status === 'cancelled');
    if (filter === 'invoiced')
      return all.filter((r) => r.status === 'invoiced' || r.status === 'invoiced_paid');
    if (filter === 'in_flight')
      return all.filter((r) =>
        ['submitted', 'scheduled', 'out_for_delivery', 'delivered'].includes(r.status),
      );
    return all;
  }, [rows, filter]);

  const totals = useMemo(() => {
    const all = rows ?? [];
    return {
      count: all.length,
      invoiced: all.filter((r) => r.status === 'invoiced' || r.status === 'invoiced_paid')
        .length,
      cancelled: all.filter((r) => r.status === 'cancelled').length,
    };
  }, [rows]);

  return (
    <div className="max-w-5xl mx-auto">
      <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">
        Orders you've placed here, with live delivery status. Once an order is
        invoiced you can pay it under Billing.
      </p>

      {reorderNotice && (
        <div
          role="status"
          className={cn(
            'mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium',
            reorderNotice.tone === 'success'
              ? 'border-brix-primary/30 bg-brix-primary-tint/60 text-brix-primary'
              : 'border-amber-200 bg-amber-50 text-amber-800',
          )}
        >
          {reorderNotice.tone === 'success' ? (
            <CheckCircle2 size={15} className="shrink-0" />
          ) : (
            <XCircle size={15} className="shrink-0" />
          )}
          <span>{reorderNotice.text}</span>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <SummaryCard label="Total entries" value={isLoading ? '—' : String(totals.count)} />
        <SummaryCard label="Invoiced" value={isLoading ? '—' : String(totals.invoiced)} />
        <SummaryCard
          label="Cancelled"
          value={isLoading ? '—' : String(totals.cancelled)}
          tone={totals.cancelled > 0 ? 'warning' : 'muted'}
        />
      </div>

      {/* Filter tabs */}
      <div className="flex justify-center gap-2 mb-5 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              'px-5 py-2 text-sm rounded-full cursor-pointer transition-all duration-200',
              filter === f.id
                ? 'bg-brix-primary-tint text-brix-primary font-semibold shadow-card'
                : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 backdrop-blur-xl overflow-hidden shadow-card-deep">
        <div className="h-1.5 bg-brix-primary" aria-hidden />
        <div className="grid grid-cols-[1.4fr_110px_130px_120px_120px_110px_28px] gap-4 px-5 py-3.5 text-[11px] font-bold tracking-tight text-brix-muted border-b border-brix-border bg-brix-surface-alt/60">
          <div>Order / Job #</div>
          <div>Date</div>
          <div>Status</div>
          <div>Invoice</div>
          <div className="text-right">Amount</div>
          <div />
          <div />
        </div>

        {isLoading ? (
          <div className="px-4 py-14 text-center text-sm text-brix-muted">
            <Loader2 size={18} className="animate-spin inline-block mr-2" />
            Loading activity…
          </div>
        ) : error ? (
          /* ⚠ A failed read must NEVER render as the empty state. "No orders
             yet" on an account with 200 orders is worse than an error: it reads
             as data loss and nobody thinks to look further. */
          <div className="px-4 py-14 text-center text-sm">
            <XCircle size={18} className="inline-block mr-2 text-amber-600" />
            <span className="text-brix-ink font-medium">
              We couldn't load your order history.
            </span>
            <div className="mt-2 text-xs text-brix-muted">
              {error instanceof Error ? error.message : 'Please try again.'}
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 rounded-full text-sm font-semibold bg-brix-primary-tint text-brix-primary cursor-pointer"
            >
              Try again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center text-sm text-brix-muted">
            {filter === 'all'
              ? "No orders yet — orders you place here will show up with live delivery status. Invoices live under Billing."
              : 'No entries in this filter.'}
          </div>
        ) : (
          filtered.map((row) => (
            <ActivityListRow
              key={row.rowId}
              row={row}
              onReorderResult={handleReorderResult}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="text-center mt-8 space-y-3">
        <p className="text-sm text-brix-muted">Question about an order or delivery?</p>
        <ContactUsMenu variant="inline" triggerLabel="Chat with us" initialChannel="email" />
      </div>
    </div>
  );
}

function ActivityListRow({
  row,
  onReorderResult,
}: {
  row: ActivityRow;
  onReorderResult: (summary: ReorderSummary | null) => void;
}) {
  const invoiceHref = row.qboInvoiceId
    ? `/invoices/${encodeURIComponent(row.qboInvoiceId)}`
    : null;
  /* ⚠ row.orderId, NOT row.rowId. `row_id` is the view's composite key
     ('order:<uuid>'), so linking with it produced /orders/order%3A<uuid> and
     an "invalid input syntax for type uuid" 400 that the order page showed as
     "We couldn't find that order." Same value was being handed to Reorder. */
  const orderHref =
    !invoiceHref && row.source === 'order' && row.orderId
      ? `/orders/${encodeURIComponent(row.orderId)}`
      : null;
  const targetHref = invoiceHref ?? orderHref;
  // Reorder only applies to portal orders (they carry order_lines keyed by
  // catalog_item_id). Invoice-sourced activity rows have no local line set.
  const canReorder = row.source === 'order' && Boolean(row.orderId);

  const reorderCell = (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      {canReorder ? (
        <ReorderButton
          orderId={row.orderId ?? ''}
          stopPropagation
          onResult={onReorderResult}
        />
      ) : null}
    </div>
  );

  /* ⚠ `order_number` is NULL on every order this business has ever taken (204
     of 204, checked live 2026-09-04) — nothing writes it, and v_activity passes
     `o.order_number` straight through with no fallback. So this heading rendered
     BLANK on every row and the SF job number below it was the only identifier a
     customer could see. Fall back to what the row actually carries. */
  const heading = rowHeading(row);

  const innerRow = (
    <>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-brix-primary-tint text-brix-primary shrink-0 shadow-card">
          {row.source === 'order' ? <ClipboardList size={18} /> : <ReceiptText size={18} />}
        </div>
        <div className="min-w-0">
          <div
            className="text-lg font-bold text-slate-900 font-mono leading-tight tracking-tight"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {heading.main}
          </div>
          {heading.sub && (
            <div
              className="text-xs text-slate-500 truncate mt-0.5 font-mono tracking-tight"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {heading.sub}
            </div>
          )}
        </div>
      </div>
      <div className="text-sm text-slate-900 font-medium">
        {fmtDate(row.activityDate)}
        {row.scheduledDate && (
          <div className="text-[11px] text-brix-muted">
            <Truck size={11} className="inline -mt-0.5 mr-0.5" />
            sched {fmtDate(row.scheduledDate)}
          </div>
        )}
      </div>
      <div>
        <StatusPill status={row.status} />
      </div>
      <div className="text-sm">
        {row.invoiceDocNumber ? (
          <span className="font-mono text-slate-900 font-medium">
            #{row.invoiceDocNumber}
          </span>
        ) : (
          <span className="text-brix-muted">—</span>
        )}
      </div>
      <div className="text-right">
        <div
          className="font-mono text-xl font-bold text-slate-900 leading-tight tracking-tight"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatUsd(row.amount)}
        </div>
      </div>
      {reorderCell}
      <ChevronRight
        size={16}
        className={cn('text-brix-muted', !targetHref && 'opacity-30')}
      />
    </>
  );

  if (targetHref) {
    return (
      <Link
        to={targetHref}
        className={cn(
          'grid grid-cols-[1.4fr_110px_130px_120px_120px_110px_28px] gap-4 px-5 py-4 items-center',
          'border-b border-brix-border last:border-b-0',
          'hover:bg-brix-primary-tint/30 transition-colors cursor-pointer',
        )}
      >
        {innerRow}
      </Link>
    );
  }

  return (
    <div className="grid grid-cols-[1.4fr_110px_130px_120px_120px_110px_28px] gap-4 px-5 py-4 items-center border-b border-brix-border last:border-b-0">
      {innerRow}
    </div>
  );
}

function StatusPill({ status }: { status: ActivityStatus }) {
  const { label, cls } = describeStatus(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-tight border',
        cls,
      )}
    >
      {status === 'cancelled' && <XCircle size={11} />}
      {(status === 'delivered' || status === 'invoiced_paid') && <CheckCircle2 size={11} />}
      {(status === 'out_for_delivery' || status === 'scheduled') && <Truck size={11} />}
      {label}
    </span>
  );
}

function describeStatus(status: ActivityStatus): { label: string; cls: string } {
  switch (status) {
    case 'cancelled':
      return { label: 'Cancelled', cls: 'bg-red-600 text-white border-red-700' };
    case 'invoiced':
      return { label: 'Invoiced', cls: 'bg-amber-50 text-amber-800 border-amber-200' };
    case 'invoiced_paid':
      return { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'delivered':
      return {
        label: 'Delivered',
        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      };
    case 'out_for_delivery':
      return {
        label: 'Out for delivery',
        cls: 'bg-blue-50 text-blue-700 border-blue-200',
      };
    case 'scheduled':
      return { label: 'Scheduled', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'submitted':
      return {
        label: 'Submitted',
        cls: 'bg-brix-primary-tint text-brix-primary border-brix-primary/30',
      };
    case 'draft':
      return { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
    default:
      return {
        label: status.replace(/_/g, ' '),
        cls: 'bg-slate-100 text-slate-600 border-slate-200',
      };
  }
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning' | 'muted';
}) {
  const valueColor =
    tone === 'warning'
      ? 'text-red-600'
      : tone === 'muted'
        ? 'text-slate-500'
        : 'text-slate-900';
  return (
    <div className="rounded-2xl border border-brix-border bg-brix-surface/85 backdrop-blur-xl px-5 py-5 shadow-card-hover hover:shadow-card-deep hover:-translate-y-0.5 transition-all duration-300 flex flex-col items-center justify-center text-center h-full">
      <div className="text-[11px] tracking-tight text-slate-500 font-bold">
        {label}
      </div>
      <div
        className={cn(
          'mt-2 font-mono font-bold text-3xl md:text-4xl leading-none tracking-tight',
          valueColor,
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}
